import React, { useEffect, useRef, useState } from 'react';
import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { fmtDate } from './historicalDate';

// Dropdown picker for a single historical Wayback release (one of typically
// 50-150 dated snapshots for a property). Used by the Swipe and Fade modes
// of HistoricalCompare. Extracted out of HistoricalCompare.jsx in Feb 2026
// to keep the parent file under the 700-line guideline.
export function DateSelect({ label, value, releases, onChange, loading, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  // When the menu opens, scroll the currently-selected option into view so the
  // user can immediately see where they are within the 100+ snapshots.
  useEffect(() => {
    if (!open || !menuRef.current || !value) return;
    const sel = menuRef.current.querySelector(`[data-testid="${testId}-option-${value.release_id}"]`);
    if (sel && typeof sel.scrollIntoView === 'function') {
      // `block: 'center'` keeps the active row visible mid-list rather than at the top edge.
      try { sel.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (_) { /* older browsers */ }
    }
  }, [open, value, testId]);
  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading || releases.length === 0}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-secondary/60 hover:bg-secondary text-xs font-semibold transition-colors disabled:opacity-50"
        title={label}
        data-testid={testId}
      >
        <span className="opacity-70 hidden sm:inline">{label}:</span>
        <span className="font-mono">{value ? fmtDate(value.date) : '—'}</span>
        <CaretDown size={11} weight="bold" />
      </button>
      {open && (
        <div ref={menuRef} className="absolute top-full mt-1 left-0 w-44 max-h-72 overflow-y-auto bg-popover border border-border rounded-md shadow-2xl z-50 py-1" data-testid={`${testId}-menu`}>
          {releases.map((r) => {
            const isSel = value?.release_id === r.release_id;
            return (
              <button
                key={r.release_id}
                type="button"
                onClick={() => { onChange(r); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors ${isSel ? 'bg-primary/15 text-primary font-bold' : 'hover:bg-secondary/80'}`}
                aria-selected={isSel}
                data-testid={`${testId}-option-${r.release_id}`}
              >
                {fmtDate(r.date)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Small bottom-corner badge with ◀ / ▶ arrows that step to the previous / next
// release. Because `releases` is sorted newest first, ◀ (older) = idx+1 and
// ▶ (newer) = idx-1. We use the same component for both corners — `align`
// flips horizontal positioning and arrow order doesn't change (older on the
// left, newer on the right, regardless of which corner).
export function DateStepper({ release, releases, onChange, align, testIdRoot }) {
  const idx = releases.findIndex((r) => r.release_id === release.release_id);
  const canOlder = idx >= 0 && idx < releases.length - 1; // step toward end of list
  const canNewer = idx > 0;                                // step toward index 0
  const goOlder = () => { if (canOlder) onChange(releases[idx + 1]); };
  const goNewer = () => { if (canNewer) onChange(releases[idx - 1]); };
  const posCls = align === 'left' ? 'left-4' : 'right-4';
  return (
    <div
      className={`absolute bottom-4 z-10 flex items-center gap-1 px-1.5 py-1 rounded-md bg-black/65 backdrop-blur-sm text-white text-xs font-semibold tracking-wide ${posCls}`}
      data-testid={`${testIdRoot}-label`}
    >
      <button
        type="button"
        onClick={goOlder}
        disabled={!canOlder}
        className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Previous (older) snapshot"
        aria-label="Previous snapshot"
        data-testid={`${testIdRoot}-prev-btn`}
      >
        <CaretLeft size={13} weight="bold" />
      </button>
      <span className="font-mono px-1.5" data-testid={`${testIdRoot}-date-text`}>{fmtDate(release.date)}</span>
      <button
        type="button"
        onClick={goNewer}
        disabled={!canNewer}
        className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Next (newer) snapshot"
        aria-label="Next snapshot"
        data-testid={`${testIdRoot}-next-btn`}
      >
        <CaretRight size={13} weight="bold" />
      </button>
    </div>
  );
}
